import React, { useEffect, useId, useRef, useState } from 'react'
import {
  AlertCircle, ArrowUp, BellOff, Check, Clock, History, Loader2, Plus, Sparkles, Trash2, X, Ban, Zap,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils'
import { useAssistantStore } from '@/stores/assistantStore'
import { CONFIRM_COPY, confirmFields, formatResetDate, type Nudge, type TranscriptItem } from '@/lib/assistant'
import { isApplePlatform } from '@/lib/platform'
import { useToastsClearOfSidePanel } from '@/components/ui/Toast'

/** The assistant's shortcut as this device writes it. */
export const ASSISTANT_SHORTCUT_LABEL = isApplePlatform() ? '⌘J' : 'Ctrl J'

const SUGGESTIONS = [
  'What’s on the schedule this week?',
  'Which quotes are waiting on a reply?',
  'Who owes us money right now?',
  'Add a note to a contact',
]

/**
 * The in-app assistant (docs/engineering/AI-ASSISTANT.md). Desktop: a docked
 * panel beside the page, so the app stays usable while it works. Phone: a
 * full-height BottomSheet with the composer in its footer, which clears the
 * home indicator and the keyboard.
 */
export function AssistantPanel() {
  const open = useAssistantStore((s) => s.open)
  const setOpen = useAssistantStore((s) => s.setOpen)
  const isMobile = useIsMobile()
  const docked = open && !isMobile
  // Docked on the right: toasts move left of it, and the phone button floats
  // above its composer instead of over the Send button.
  useToastsClearOfSidePanel(docked)
  useEffect(() => {
    if (!docked) return
    const root = document.documentElement
    const before = root.style.getPropertyValue('--softphone-fab-bottom')
    root.style.setProperty('--softphone-fab-bottom', '7.5rem')
    return () => {
      if (before) root.style.setProperty('--softphone-fab-bottom', before)
      else root.style.removeProperty('--softphone-fab-bottom')
    }
  }, [docked])

  if (isMobile) {
    return (
      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        title={<PanelTitle />}
        snapPoints={['full']}
        footer={<Composer autoFocus={false} />}
        ariaLabel="Assistant"
      >
        <PanelBody />
      </BottomSheet>
    )
  }

  if (!open) return null
  return (
    <aside
      aria-label="Assistant"
      className="hidden md:flex flex-col w-[400px] lg:w-[420px] shrink-0 h-screen hairline-l animate-fade-up"
      style={{ background: 'var(--midnight-bg2, #070709)' }}
      data-testid="assistant-panel"
    >
      <div className="flex items-center gap-2 px-4 pt-4 pb-3 hairline-b">
        <PanelTitle />
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close assistant"
          className="ds-btn ds-btn-icon ds-btn-ghost ml-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
        <PanelBody />
      </div>
      <div className="px-4 pt-3 pb-4 hairline-t">
        <Composer autoFocus />
      </div>
    </aside>
  )
}

function PanelTitle() {
  const newConversation = useAssistantStore((s) => s.newConversation)
  const loadHistory = useAssistantStore((s) => s.loadHistory)
  const busy = useAssistantStore((s) => s.busy)
  const [showHistory, setShowHistory] = useState(false)

  return (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <span className="ds-ico tn-neutral w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0" aria-hidden>
        <Sparkles className="w-4 h-4 text-white" />
      </span>
      <span className="text-[15px] font-semibold text-white truncate">Assistant</span>
      <div className="ml-auto flex items-center gap-1 relative">
        <button
          type="button"
          className="ds-btn ds-btn-icon ds-btn-ghost"
          aria-label="Past conversations"
          aria-expanded={showHistory}
          onClick={() => {
            if (!showHistory) void loadHistory()
            setShowHistory((v) => !v)
          }}
        >
          <History className="w-4 h-4" />
        </button>
        <button
          type="button"
          className="ds-btn ds-btn-icon ds-btn-ghost"
          aria-label="New conversation"
          disabled={busy}
          onClick={() => {
            newConversation()
            setShowHistory(false)
          }}
        >
          <Plus className="w-4 h-4" />
        </button>
        {showHistory && <HistoryList onClose={() => setShowHistory(false)} />}
      </div>
    </div>
  )
}

function HistoryList({ onClose }: { onClose: () => void }) {
  const history = useAssistantStore((s) => s.history)
  const openConversation = useAssistantStore((s) => s.openConversation)
  const removeConversation = useAssistantStore((s) => s.removeConversation)
  const current = useAssistantStore((s) => s.conversationId)

  return (
    <div
      role="dialog"
      aria-label="Past conversations"
      className="ds-tile absolute right-0 top-10 z-10 w-72 max-h-80 overflow-y-auto p-2"
      onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
    >
      {history.length === 0 ? (
        <p className="px-2 py-3 text-sm text-white/[0.62]">No past conversations yet.</p>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {history.map((h) => (
            <li key={h.id} className="flex items-center gap-1">
              <button
                type="button"
                className={cn(
                  'flex-1 min-w-0 text-left px-2 py-2 rounded-lg text-sm truncate hover:bg-white/[0.06]',
                  h.id === current ? 'text-white' : 'text-white/[0.72]',
                )}
                onClick={() => {
                  void openConversation(h.id)
                  onClose()
                }}
              >
                {h.title}
              </button>
              <button
                type="button"
                className="ds-btn ds-btn-icon ds-btn-ghost"
                aria-label={`Delete “${h.title}”`}
                onClick={() => void removeConversation(h.id)}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function UsageNotice() {
  const usage = useAssistantStore((s) => s.usage)
  const configured = useAssistantStore((s) => s.configured)
  if (configured === false) {
    return (
      <div className="ds-tile p-3 text-sm text-white/[0.72] flex gap-2" role="status">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>The assistant isn’t switched on for this workspace yet.</span>
      </div>
    )
  }
  if (!usage) return null
  if (usage.paused) {
    return (
      <div className="ds-tile p-3 text-sm text-white flex gap-2" role="status" data-testid="assistant-paused">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          The assistant is paused for this month’s fair use. It’s back on {formatResetDate(usage.resetsOn)}, or sooner if you move up a plan.
          Your AI front desk keeps answering calls.
        </span>
      </div>
    )
  }
  if (usage.nearLimit && usage.percentUsed !== null) {
    return (
      <div className="ds-tile p-3 text-sm text-white/[0.72] flex gap-2" role="status">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>You’ve used {usage.percentUsed}% of this month’s assistant use. It resets on {formatResetDate(usage.resetsOn)}.</span>
      </div>
    )
  }
  return null
}

function PanelBody() {
  const items = useAssistantStore((s) => s.items)
  const busy = useAssistantStore((s) => s.busy)
  const send = useAssistantStore((s) => s.send)
  const usage = useAssistantStore((s) => s.usage)
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' })
  }, [items, busy])

  const waiting = busy && items[items.length - 1]?.type !== 'assistant'

  return (
    <div className="flex flex-col gap-3" aria-live="polite" aria-busy={busy}>
      <UsageNotice />
      {items.length === 0 && <NudgeStack />}
      {items.length === 0 && (
        <div className="animate-fade-up flex flex-col gap-3 pt-2">
          <p className="text-sm text-white/[0.72]">
            Ask a question about your business, or tell me what to do. I’ll check with you before I text or email a customer, touch money, delete anything or change settings.
          </p>
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                className="ds-chip"
                disabled={busy || usage?.paused}
                onClick={() => void send(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
      {items.map((item, i) => (
        <Item key={'id' in item ? item.id : `${item.type}-${i}`} item={item} />
      ))}
      {waiting && (
        <div className="flex items-center gap-2 text-sm text-white/[0.62]" role="status">
          <Loader2 className="w-4 h-4 animate-spin" /> Working…
        </div>
      )}
      <div ref={endRef} />
    </div>
  )
}

/**
 * Proactive suggestions (Settings → Ask AI → Proactive). Short, like a text;
 * Do it hands the instruction to a fresh conversation, which still stops on
 * a Confirm card before anything reaches a customer or moves money.
 */
function NudgeStack() {
  const nudges = useAssistantStore((s) => s.nudges)
  if (nudges.length === 0) return null
  return (
    <section aria-label="Suggestions" className="flex flex-col gap-2" data-testid="assistant-nudges">
      <p className="text-[12px] font-medium uppercase tracking-[0.08em] text-white/[0.52]">For you</p>
      {nudges.slice(0, 6).map((n) => <NudgeCard key={n.id} n={n} />)}
    </section>
  )
}

function NudgeCard({ n }: { n: Nudge }) {
  const act = useAssistantStore((s) => s.actNudge)
  const busy = useAssistantStore((s) => s.busy)
  const paused = useAssistantStore((s) => s.usage?.paused)
  const navigate = useNavigate()
  return (
    <article className="ds-tile p-3 flex flex-col gap-2.5 animate-fade-up" data-testid="assistant-nudge">
      <p className="text-[14px] leading-5 text-white">{n.text}</p>
      <div className="flex items-center gap-1.5 flex-wrap">
        {n.ask && (
          <button
            type="button"
            className="ds-btn ds-btn-sm ds-btn-primary"
            disabled={busy || paused}
            onClick={() => void act(n.id, 'do')}
          >
            <Zap className="w-3.5 h-3.5" /> {n.actionLabel ?? 'Do it'}
          </button>
        )}
        {n.link && (
          <button
            type="button"
            className="ds-btn ds-btn-sm ds-btn-ghost"
            onClick={() => {
              navigate(n.link!)
              if (!n.ask) void act(n.id, 'done')
            }}
          >
            Open
          </button>
        )}
        <span className="ml-auto flex items-center gap-0.5">
          <button type="button" className="ds-btn ds-btn-icon ds-btn-ghost" aria-label="Later (tomorrow)" title="Later" onClick={() => void act(n.id, 'snooze')}>
            <Clock className="w-3.5 h-3.5" />
          </button>
          <button type="button" className="ds-btn ds-btn-icon ds-btn-ghost" aria-label="Not this kind" title="Not this kind" onClick={() => void act(n.id, 'mute')}>
            <BellOff className="w-3.5 h-3.5" />
          </button>
          <button type="button" className="ds-btn ds-btn-icon ds-btn-ghost" aria-label="Dismiss" title="Dismiss" onClick={() => void act(n.id, 'dismiss')}>
            <X className="w-3.5 h-3.5" />
          </button>
        </span>
      </div>
    </article>
  )
}

function Item({ item }: { item: TranscriptItem }) {
  switch (item.type) {
    case 'user':
      return (
        <div className="self-end max-w-[85%] rounded-2xl rounded-br-md bg-white/[0.10] px-3.5 py-2.5 text-sm text-white whitespace-pre-wrap break-words">
          {item.text}
        </div>
      )
    case 'assistant':
      return <div className="text-sm leading-relaxed text-white/[0.9] whitespace-pre-wrap break-words">{item.text}</div>
    case 'error':
      return (
        <div className="flex gap-2 text-sm" style={{ color: 'var(--midnight-blush, #F4B5BD)' }} role="alert">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {item.text}
        </div>
      )
    case 'tool':
      return <ToolCard item={item} />
    case 'confirm':
      return <ConfirmCard item={item} />
  }
}

function ToolCard({ item }: { item: Extract<TranscriptItem, { type: 'tool' }> }) {
  const title = item.title ?? item.name.replace(/_/g, ' ')
  return (
    <div className="ds-tile px-3 py-2 flex items-start gap-2 text-[13px]" data-testid="assistant-tool-card">
      <span className="mt-0.5 shrink-0">
        {item.status === 'running' && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/[0.62]" />}
        {item.status === 'ok' && <Check className="w-3.5 h-3.5" style={{ color: 'var(--midnight-mint, #A8E6CE)' }} />}
        {item.status === 'error' && <AlertCircle className="w-3.5 h-3.5" style={{ color: 'var(--midnight-blush, #F4B5BD)' }} />}
        {(item.status === 'cancelled' || item.status === 'awaiting') && <Ban className="w-3.5 h-3.5 text-white/[0.62]" />}
      </span>
      <span className="min-w-0">
        <span className="text-white/[0.85]">{title}</span>
        {item.status === 'cancelled' && <span className="text-white/[0.62]"> · not done</span>}
        {item.status === 'error' && item.summary && <span className="block text-white/[0.62]">{item.summary}</span>}
      </span>
    </div>
  )
}

function ConfirmCard({ item }: { item: Extract<TranscriptItem, { type: 'confirm' }> }) {
  const decide = useAssistantStore((s) => s.decide)
  const busy = useAssistantStore((s) => s.busy)
  const copy = CONFIRM_COPY[item.kind]
  const fields = confirmFields(item.input)
  const headingId = useId()

  return (
    <section className="ds-tile p-3.5 flex flex-col gap-3 animate-fade-up" aria-labelledby={headingId} data-testid="assistant-confirm-card">
      <div className="flex items-center gap-2">
        <span className="ds-chip">{copy.badge}</span>
      </div>
      <h3 id={headingId} className="text-sm font-semibold text-white">{item.title}</h3>
      {item.target && <p className="text-[13px] text-white/[0.85] -mt-1.5" data-testid="assistant-confirm-target">{item.target}</p>}
      {fields.length > 0 && (
        <dl className="flex flex-col gap-2 text-[13px]">
          {fields.map((f) => (
            <div key={f.label} className={f.long ? 'flex flex-col gap-1' : 'flex gap-2'}>
              <dt className="text-white/[0.62] shrink-0">{f.label}</dt>
              <dd className={cn('text-white break-words', f.long && 'whitespace-pre-wrap rounded-lg bg-black/40 px-2.5 py-2')}>{f.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          className="ds-btn ds-btn-sm ds-btn-ghost"
          disabled={busy}
          onClick={() => void decide(item.id, 'cancel')}
        >
          Cancel
        </button>
        <button
          type="button"
          className="ds-btn ds-btn-sm ds-btn-primary"
          disabled={busy}
          onClick={() => void decide(item.id, 'confirm')}
        >
          {item.deciding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {copy.action}
        </button>
      </div>
    </section>
  )
}

function Composer({ autoFocus }: { autoFocus: boolean }) {
  const send = useAssistantStore((s) => s.send)
  const busy = useAssistantStore((s) => s.busy)
  const usage = useAssistantStore((s) => s.usage)
  const configured = useAssistantStore((s) => s.configured)
  const [text, setText] = useState('')
  const ref = useRef<HTMLTextAreaElement>(null)
  const inputId = useId()
  const disabled = Boolean(usage?.paused) || configured === false

  useEffect(() => {
    if (autoFocus) ref.current?.focus()
  }, [autoFocus])

  const submit = () => {
    if (!text.trim() || busy || disabled) return
    void send(text)
    setText('')
  }

  return (
    <form
      className="flex items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <label htmlFor={inputId} className="sr-only">Message the assistant</label>
      <textarea
        id={inputId}
        ref={ref}
        rows={2}
        maxLength={4000}
        className="input-field flex-1 min-w-0 resize-none text-base sm:text-sm"
        placeholder={disabled ? 'The assistant is paused' : 'Ask or tell me what to do…'}
        value={text}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault()
            submit()
          }
        }}
      />
      <button
        type="submit"
        className="ds-btn ds-btn-lg ds-btn-primary shrink-0"
        aria-label="Send to assistant"
        disabled={!text.trim() || busy || disabled}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
      </button>
    </form>
  )
}

/** ⌘J / Ctrl J toggles the panel from anywhere in the app. */
export function useAssistantShortcut() {
  const toggle = useAssistantStore((s) => s.toggle)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'j') {
        e.preventDefault()
        toggle()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [toggle])
}

/** Sidebar / header button. */
export function AssistantButton({ variant, collapsed = false }: { variant: 'sidebar' | 'topbar'; collapsed?: boolean }) {
  const toggle = useAssistantStore((s) => s.toggle)
  const open = useAssistantStore((s) => s.open)
  const count = useNudgeCount()
  const badge = count > 0 && !open && (
    <span
      className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#00F030] text-black text-[10px] font-bold leading-4 text-center"
      data-testid="assistant-nudge-badge"
    >
      {count > 9 ? '9+' : count}
    </span>
  )
  const waiting = count > 0 ? `, ${count} suggestion${count === 1 ? '' : 's'}` : ''
  if (variant === 'topbar') {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-label={`Open assistant${waiting}`}
        aria-pressed={open}
        className="relative w-11 h-11 flex items-center justify-center text-ink-300 hover:text-ink-50 hover:bg-white/[0.04] rounded-full transition-all shrink-0"
      >
        <Sparkles className="w-5 h-5" />
        {badge}
      </button>
    )
  }
  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={open}
      aria-label={`Assistant${waiting}`}
      title={collapsed ? `Assistant (${ASSISTANT_SHORTCUT_LABEL})` : undefined}
      className={cn(
        'group relative w-full flex items-center gap-[11px] h-9 coarse:h-11 px-3 rounded-xl text-[13.5px] font-medium transition-colors duration-200',
        open ? 'nav-pill-active text-white' : 'text-white/[0.66] hover:text-white hover:bg-white/[0.06]',
        collapsed && 'justify-center px-0',
      )}
    >
      <Sparkles className={cn('w-4 h-4 shrink-0', open ? 'text-white' : 'text-white/[0.62] group-hover:text-white/80')} />
      {!collapsed && (
        <>
          <span className="flex-1 text-left truncate">Assistant</span>
          <kbd className="font-mono text-xs leading-5 px-1.5 rounded-md border border-white/[0.16] bg-white/[0.06] text-white/[0.62]">
            {ASSISTANT_SHORTCUT_LABEL}
          </kbd>
        </>
      )}
      {badge}
    </button>
  )
}

/**
 * How many suggestions are waiting. Checks every minute and when the tab comes
 * back, and opens the panel when a bell row or push linked here (?assistant=1).
 */
function useNudgeCount(): number {
  const count = useAssistantStore((s) => s.nudges.length)
  const refresh = useAssistantStore((s) => s.refreshNudges)
  const setOpen = useAssistantStore((s) => s.setOpen)
  useEffect(() => {
    void refresh()
    const id = window.setInterval(() => void refresh(), 60_000)
    const onFocus = () => void refresh()
    window.addEventListener('focus', onFocus)
    const params = new URLSearchParams(window.location.search)
    if (params.get('assistant') === '1') {
      setOpen(true)
      params.delete('assistant')
      const qs = params.toString()
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`)
    }
    return () => {
      window.clearInterval(id)
      window.removeEventListener('focus', onFocus)
    }
  }, [refresh, setOpen])
  return count
}

export default AssistantPanel
